'use client';

import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useRef, useState } from 'react';

import { parseAwsAccountsCsv } from '@/utils/awsOrgCsv';

export const CsvDropZone = ({
  onAccounts,
}: {
  onAccounts: (accountIds: string[]) => void;
}) => {
  const [fileName, setFileName] = useState<string | null>(null);
  const [accountCount, setAccountCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    (file: File) => {
      setError(null);
      if (!file.name.endsWith('.csv')) {
        setError('Please upload a CSV file.');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result;
        if (typeof text !== 'string') return;

        try {
          const accountIds = parseAwsAccountsCsv(text);
          setFileName(file.name);
          setAccountCount(accountIds.length);
          onAccounts(accountIds);
        } catch (err) {
          setFileName(null);
          setAccountCount(0);
          setError(
            err instanceof Error ? err.message : 'Failed to parse the CSV file.'
          );
          onAccounts([]);
        }
      };
      reader.onerror = () => {
        setError(
          `Could not read the file: ${reader.error?.message ?? 'unknown error'}.`
        );
      };
      reader.readAsText(file);
    },
    [onAccounts]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  return (
    <Stack gap='8px'>
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: '2px dashed var(--mui-palette-divider)',
          borderRadius: '8px',
          padding: '32px',
          textAlign: 'center',
          cursor: 'pointer',
          '&:hover': { borderColor: 'var(--mui-palette-primary-main)' },
        }}
      >
        <input
          ref={fileInputRef}
          type='file'
          accept='.csv'
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
          }}
        />
        <Stack gap='8px' alignItems='center'>
          <i
            className='material-symbols-upload-file'
            style={{ color: 'var(--mui-palette-text-secondary)' }}
          />
          {fileName ? (
            <>
              <Typography variant='subtitle1'>{fileName}</Typography>
              <Typography variant='body2'>
                {accountCount} active account{accountCount !== 1 ? 's' : ''}{' '}
                found
              </Typography>
            </>
          ) : (
            <>
              <Typography variant='subtitle1'>
                Drop your CSV file here or click to browse
              </Typography>
            </>
          )}
        </Stack>
      </Box>
      {error && (
        <Typography variant='body2' color='error'>
          {error}
        </Typography>
      )}
    </Stack>
  );
};
